import type { ComputedRef, Ref } from 'vue';
import type { DxDataGrid } from 'devextreme-vue/data-grid';
import type { DateRangeShortcut } from '~/types';
import {
  getDateFilterFromLocalStorage,
  saveDateFilterInLocalStorage,
  removeShortcutFromLocalStorage,
  getShortcutFromLocalStorage
} from '~/utils/data-grid.storage';

type DxInstance = NonNullable<InstanceType<typeof DxDataGrid>['instance']>;

export interface UseDataGridDateRangeOptions {
  dataGrid: ComputedRef<DxInstance | undefined>;
  shortcuts: Ref<Array<DateRangeShortcut>>;
  dateFormat: ComputedRef<string>;
}

export const useDataGridDateRange = ({
  dataGrid,
  shortcuts,
  dateFormat
}: UseDataGridDateRangeOptions) => {
  const { $i18n } = useNuxtApp();
  const { locale } = $i18n;
  const { dateRange } = storeToRefs(useStore());

  const getDefaultDaysRange = (): number => {
    const config = useRuntimeConfig();
    return Number(config.public.defaultDaysRangeTransactionsGrid);
  };

  const getWalletTransactionsDefaultRange = () => {
    const start = getFormattedDate(
      getDate().add(-12, 'month').add(1, 'day').startOf('day'),
      dateFormat.value,
      locale.value
    );
    const end = getFormattedDate(getDate().endOf('day'), dateFormat.value, locale.value);
    return [start, end];
  };

  const resolveShortcutRange = (
    savedShortcut: string
  ): { iso: string[]; display: string[] } | undefined => {
    const found = shortcuts.value.find((shortcut) => shortcut.dateShortcut === savedShortcut);
    if (!found?.range || found.range.length !== 2) return undefined;

    const startISO = toISOLocal(getDate(found.range[0]).startOf('day').toDate());
    const endISO = toISOLocal(getDate(found.range[1]).endOf('day').toDate());

    return {
      iso: [startISO, endISO],
      display: [
        getFormattedDate(getDate(startISO), dateFormat.value, locale.value),
        getFormattedDate(getDate(endISO), dateFormat.value, locale.value)
      ]
    };
  };

  const initDateRange = (itemName: string): string[] => {
    const defaultDaysRange = getDefaultDaysRange();
    const isWalletTransactions = itemName.includes('all-wallet-transactions');
    const savedShortcut = getShortcutFromLocalStorage(itemName) || '';
    let localDateRange = getDateFilterFromLocalStorage(itemName);

    if (savedShortcut) {
      const resolved = resolveShortcutRange(savedShortcut);
      if (resolved) {
        saveDateFilterInLocalStorage(resolved.iso, itemName);
        return resolved.display;
      }
    }

    if ((!localDateRange && isWalletTransactions) || localDateRange?.length === 0) {
      return getWalletTransactionsDefaultRange();
    }

    if (!localDateRange) {
      const date = getDate();
      localDateRange = [
        toISOLocal(date.add(-defaultDaysRange, 'day').startOf('day').toDate()),
        toISOLocal(date.endOf('day').toDate())
      ];
      saveDateFilterInLocalStorage(localDateRange, itemName);
    }

    return [
      getFormattedDate(getDate(localDateRange[0]), dateFormat.value, locale.value),
      getFormattedDate(getDate(localDateRange[1]), dateFormat.value, locale.value)
    ];
  };

  const updateDate = (
    newRange: Array<string>,
    stateStorageName: string,
    defaultFilter?: Array<Array<string> | string>,
    isAccountList?: boolean,
    dateColumnName: string = 'date'
  ) => {
    shortcuts.value = shortcuts.value.map((s) => ({ ...s, selected: false }));
    dateRange.value = newRange;

    const createdAtFrom = getDate(newRange[0], dateFormat.value, locale.value);
    const createdAtTo = getDate(newRange[1], dateFormat.value, locale.value).endOf('date');

    const formatedDateRange = isAccountList
      ? [
          `createdAtFrom:${createdAtFrom.format('YYYY-MM-DD')}`,
          `createdAtTo:${createdAtTo.format('YYYY-MM-DD')}`
        ]
      : [toISOLocal(createdAtFrom.toDate()), toISOLocal(createdAtTo.toDate())];

    saveDateFilterInLocalStorage(
      isAccountList
        ? [
            createdAtFrom.format('DD-MM-YYYY').toString(),
            createdAtTo.format('DD-MM-YYYY').toString()
          ]
        : [formatedDateRange[0], formatedDateRange[1]],
      stateStorageName
    );

    const newFilters = isAccountList
      ? [
          dateColumnName,
          `createdAtFrom:${createdAtFrom.format('YYYY-MM-DD')}`,
          `createdAtTo:${createdAtTo.format('YYYY-MM-DD')}`
        ]
      : [
          [dateColumnName, '>=', formatedDateRange[0]],
          'and',
          [dateColumnName, '<=', formatedDateRange[1]]
        ];
    if (defaultFilter) {
      newFilters.push(...['and', ...defaultFilter]);
    }

    dataGrid.value?.filter(newFilters);
  };

  const resetDateRange = (
    storageName: string,
    emptyValue = false,
    defaultFilter?: Array<Array<string> | string>,
    dateColumnName: string = 'date'
  ) => {
    if (storageName.includes('account-list-date')) {
      const date = getDate();
      const startDisplay = getFormattedDate(
        date.subtract(1, 'year').startOf('day'),
        dateFormat.value,
        locale.value
      );
      const endDisplay = getFormattedDate(date.endOf('day'), dateFormat.value, locale.value);

      updateDate([startDisplay, endDisplay], storageName, defaultFilter, true, dateColumnName);
      removeShortcutFromLocalStorage(storageName);
      return;
    }

    const defaultDaysRange = getDefaultDaysRange();

    if (storageName.includes('all-wallet-transactions')) {
      const [start, end] = getWalletTransactionsDefaultRange();
      updateDate([start, end], storageName, defaultFilter, false, dateColumnName);
      removeShortcutFromLocalStorage(storageName);
      return;
    }

    if (emptyValue) {
      dateRange.value = [];
      saveDateFilterInLocalStorage(dateRange.value, storageName);
      removeShortcutFromLocalStorage(storageName);
      return;
    }

    const date = getDate();
    const startISO = toISOLocal(date.add(-defaultDaysRange, 'day').startOf('day').toDate());
    const endISO = toISOLocal(date.endOf('day').toDate());
    const startDisplay = getFormattedDate(startISO, dateFormat.value, locale.value);
    const endDisplay = getFormattedDate(endISO, dateFormat.value, locale.value);

    updateDate([startDisplay, endDisplay], storageName, defaultFilter, false, dateColumnName);
    removeShortcutFromLocalStorage(storageName);
  };

  return {
    initDateRange,
    updateDate,
    resetDateRange,
    resolveShortcutRange,
    getDateFilterFromLocalStorage,
    saveDateFilterInLocalStorage
  };
};
